import { Link } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader, CardNote, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/empty-state";
import { Loading, LoadFailure } from "@/components/query-state";
import { Stat } from "@/components/stat";
import { StatusChip, type Tone } from "@/components/status";
import { TBody, TD, TH, THead, TR, Table } from "@/components/ui/table";
import { describeOverdue, type OverdueLevel } from "@/lib/health";
import { useRuns, useSummary } from "@/lib/queries";
import { runFailed } from "@/lib/schemas";
import { formatClock, formatRelative } from "@/lib/time";
import { useNow } from "@/lib/use-now";
import { useOverdueSignal } from "@/lib/use-overdue";

const RECENT_FAILURE_LIMIT = 100;
const FAILURES_SHOWN = 8;

const OVERDUE_TONE: Record<OverdueLevel, Tone> = {
  normal: "muted",
  watch: "warn",
  elevated: "danger",
};

export function OverviewPage() {
  const now = useNow();
  const summary = useSummary();
  const runs = useRuns({ limit: RECENT_FAILURE_LIMIT });

  const overdue = useOverdueSignal(
    summary.data?.alertsOverdue,
    summary.data?.asOf,
    summary.data?.alertsActive ?? 0,
  );

  const failures = (runs.data ?? []).filter(runFailed).slice(0, FAILURES_SHOWN);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-base font-semibold">Overview</h1>
        {summary.data !== undefined ? (
          <span className="text-xs text-muted-foreground" data-numeric>
            Updated {formatClock(summary.data.asOf)}
          </span>
        ) : null}
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto"
          onClick={() => {
            void summary.refetch();
            void runs.refetch();
          }}
        >
          <RefreshCw aria-hidden="true" className="h-3.5 w-3.5" />
          Refresh
        </Button>
      </div>

      {summary.isPending ? <Loading what="the summary" /> : null}
      {summary.error !== null ? (
        <LoadFailure what="the summary" error={summary.error} onRetry={() => void summary.refetch()} />
      ) : null}

      {summary.data !== undefined ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Stat
            label="Alerts active"
            value={summary.data.alertsActive}
            caption={`${summary.data.alertsTotal} total`}
            data-testid="stat-active"
          />

          <Stat
            label="Runs · last hour"
            value={summary.data.runsLastHour}
            caption={
              summary.data.runsLastHour === 0
                ? "no worker has run anything"
                : `${summary.data.failuresLastHour} failed (${percent(summary.data.failuresLastHour, summary.data.runsLastHour)})`
            }
            tone={summary.data.failuresLastHour > 0 ? "warn" : "ok"}
            data-testid="stat-runs"
          />

          <Stat
            label="Matches · last hour"
            value={summary.data.matchesLastHour}
            data-testid="stat-matches"
          />

          {/*
            The one figure on this page that is always non-zero on a healthy
            system. It is toned by the sustained signal, never by the raw
            count, so an ordinary reading is stated as ordinary.
          */}
          <Stat
            label="Alerts overdue"
            value={summary.data.alertsOverdue}
            tone={OVERDUE_TONE[overdue.level]}
            caption={
              // The chip carries the level; the sentence behind it is a
              // tooltip. An ordinary overdue count is the common case, and a
              // paragraph explaining why it is ordinary appears on every
              // healthy reading, which is most of them.
              <StatusChip
                tone={OVERDUE_TONE[overdue.level]}
                icon={overdue.level === "normal" ? "pause" : "default"}
                className="self-start"
                title={describeOverdue(overdue.level)}
              >
                {overdue.level === "normal"
                  ? "normal"
                  : overdue.level === "watch"
                    ? "above usual"
                    : "backlog"}
              </StatusChip>
            }
            data-level={overdue.level}
            data-testid="stat-overdue"
          />
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Recent failures</CardTitle>
          <CardNote>
            <Link className="underline underline-offset-4" to="/runs">
              Full run log
            </Link>
          </CardNote>
        </CardHeader>

        {runs.isPending ? <Loading what="recent runs" /> : null}
        {runs.error !== null ? (
          <LoadFailure what="recent runs" error={runs.error} onRetry={() => void runs.refetch()} />
        ) : null}

        {runs.data !== undefined && failures.length === 0 ? (
          <EmptyState title="No failures in the last 100 runs.">
            Open the run log to see the rest.
          </EmptyState>
        ) : null}

        {failures.length > 0 ? (
          <CardBody className="p-0">
            <Table>
              <THead>
                <TR>
                  <TH>When</TH>
                  <TH>Alert</TH>
                  <TH>Error</TH>
                  <TH className="text-right">Seen</TH>
                </TR>
              </THead>
              <TBody>
                {failures.map((run) => (
                  <TR key={run.id}>
                    <TD data-numeric className="whitespace-nowrap text-muted-foreground">
                      {formatRelative(run.ranAt, now)}
                    </TD>
                    <TD>
                      <Link
                        className="font-mono text-xs underline underline-offset-4"
                        to={`/runs?alertId=${run.alertId}`}
                      >
                        {run.alertId.slice(0, 8)}
                      </Link>
                    </TD>
                    <TD>
                      <StatusChip tone="danger">{run.errorKind ?? run.outcome}</StatusChip>
                    </TD>
                    <TD data-numeric className="text-right">
                      {run.listingsSeen}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </CardBody>
        ) : null}
      </Card>
    </div>
  );
}

function percent(part: number, whole: number): string {
  if (whole === 0) return "0%";
  const value = (part / whole) * 100;
  return `${value < 10 ? value.toFixed(1) : Math.round(value)}%`;
}
